import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { admin, type AccessGrid, type AccessSource } from "../lib/api";
import { useStudio } from "../lib/context";
import { ErrorBox, errorMessage, flagOf, Loading } from "../components/ui";

const SOURCE: Record<AccessSource, string> = { country: "propre à ce pays", global: "tous les pays", default: "valeur par défaut" };

/**
 * Formules : ce que chaque situation (sans abonnement, essai, délai de grâce, chaque formule) débloque.
 * Réglage pour tous les pays, avec exceptions par pays. Effet en moins d'une minute, chaque changement journalisé.
 * Le serveur applique la grille ; l'app montre un cadenas pour ce qui n'est pas compris.
 */
export function AccessTab({ canEdit }: { canEdit: boolean }) {
  const { toast } = useStudio();
  const qc = useQueryClient();
  const [scope, setScope] = useState("*");
  const countries = useQuery({ queryKey: ["admin-countries"], queryFn: admin.countries });
  const key = ["admin-access", scope];
  const grid = useQuery({ queryKey: key, queryFn: () => admin.access(scope) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-access"] });

  const set = useMutation({
    mutationFn: admin.setAccess,
    onMutate: (b) => {
      // Affichage immédiat (la case change au clic) ; rétabli si le serveur refuse.
      const prev = qc.getQueryData<AccessGrid>(key);
      if (prev) qc.setQueryData<AccessGrid>(key, { ...prev, grid: { ...prev.grid, [b.tier]: { ...prev.grid[b.tier], [b.feature]: { enabled: b.enabled, limit: b.limit, source: scope === "*" ? "global" : "country" } } } });
      void qc.cancelQueries({ queryKey: key });
      return { prev };
    },
    onError: (e, _b, c) => { if (c?.prev) qc.setQueryData(key, c.prev); toast(errorMessage(e), "err"); },
    onSuccess: () => toast("Enregistré : appliqué dans l'app en moins d'une minute"),
    onSettled: () => void refresh(),
  });
  const reset = useMutation({ mutationFn: admin.resetAccess, onSuccess: () => { toast("Valeur héritée rétablie"); void refresh(); }, onError: (e) => toast(errorMessage(e), "err") });

  if (grid.isLoading) return <Loading />;
  if (grid.error) return <ErrorBox error={grid.error} onRetry={() => grid.refetch()} />;
  const g = grid.data!;

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="row wrap" style={{ gap: 12 }}>
        <div className="seg" role="group" aria-label="Portée des règles">
          <button type="button" aria-pressed={scope === "*"} onClick={() => setScope("*")}>Tous les pays</button>
          {countries.data?.map((c) => (
            <button key={c.id} type="button" aria-pressed={scope === c.id} onClick={() => setScope(c.id)}>{c.flag ?? flagOf(c.id)} {c.name}</button>
          ))}
        </div>
        <span className="spacer" />
        <div className="legend">
          {scope !== "*" && <span><i style={{ background: "var(--accent-soft)", border: "1px solid var(--accent-line)" }} />propre à ce pays</span>}
          <span><i style={{ background: scope === "*" ? "var(--accent-soft)" : "var(--info-soft)", border: "1px solid var(--border)" }} />{scope === "*" ? "réglé pour tous les pays" : "hérité de « tous les pays »"}</span>
          <span><i style={{ background: "var(--surface)", border: "1px solid var(--border)" }} />valeur par défaut</span>
        </div>
      </div>
      <p className="muted small">Le serveur applique ces règles ; dans l'app, ce qui n'est pas compris apparaît avec un cadenas et l'invitation à s'abonner. <b>Tous les pays</b> sert de base ; un pays peut avoir ses exceptions.</p>
      {g.country && <Durations countryId={scope} trialDays={g.country.trialDays} graceDays={g.country.graceDays} canEdit={canEdit} onSaved={refresh} />}

      <div className="card pad-0 table-wrap">
        <table className="t matrix" style={{ minWidth: 960 }}>
          <thead>
            <tr>
              <th style={{ width: 260 }}>Fonctionnalité</th>
              {g.tiers.map((t) => <th key={t.id} style={{ textAlign: "center", whiteSpace: "normal", minWidth: 104 }}>{t.label}{t.kind === "plan" ? "" : <div className="muted" style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, marginTop: 2 }}>{t.id === "FREE" ? "ou abonnement expiré" : t.id === "TRIAL" ? "nouvelle famille" : "après la fin payée"}</div>}</th>)}
            </tr>
          </thead>
          <tbody>
            {g.features.map((f) => (
              <tr key={f.id}>
                <td>
                  <b>{f.label}</b>{f.where === "app" ? <span className="badge plain b-INFO" style={{ marginLeft: 6 }}>contrôlé dans l'app</span> : null}
                  <div className="muted tiny" style={{ maxWidth: 300 }}>{f.description}</div>
                </td>
                {g.tiers.map((t) => {
                  const c = g.grid[t.id]![f.id]!;
                  const inherited = scope !== "*" ? c.source !== "country" : c.source === "default";
                  return (
                    <td key={t.id} className={`cell ${c.source === "country" || (scope === "*" && c.source === "global") ? "own" : c.source === "global" ? "global" : ""}`}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: canEdit ? "pointer" : "default" }} title={SOURCE[c.source]}>
                        <input type="checkbox" className="switch" checked={c.enabled} disabled={!canEdit}
                          aria-label={`${f.label} — ${t.label}`}
                          onChange={(e) => set.mutate({ scope, tier: t.id, feature: f.id, enabled: e.target.checked, limit: e.target.checked ? c.limit : null })} />
                      </label>
                      {f.limit && c.enabled ? (
                        <LimitInput value={c.limit} max={f.limit.max} label={f.limit.label} disabled={!canEdit}
                          onSave={(limit) => set.mutate({ scope, tier: t.id, feature: f.id, enabled: true, limit })} />
                      ) : null}
                      <div className="muted" style={{ fontSize: 10.5, marginTop: 4, minHeight: 14 }}>
                        {c.source === "default" ? null : SOURCE[c.source]}
                        {canEdit && !inherited ? <> · <button className="linklike" style={{ fontSize: 10.5 }} onClick={() => reset.mutate({ scope, tier: t.id, feature: f.id })} title={scope === "*" ? "Revenir à la valeur par défaut" : "Revenir au réglage « tous les pays »"}>rétablir</button></> : null}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        Les limites s'entendent par élève et par heure ; vide = sans autre limite que la protection générale du service.
        Les chapitres déjà téléchargés restent lisibles même si les téléchargements ne sont plus compris.
      </p>
      {!canEdit && <span className="muted small">Lecture seule : les formules sont réservées à l'administrateur.</span>}
    </div>
  );
}

function LimitInput({ value, max, label, disabled, onSave }: { value: number | null; max: number; label: string; disabled: boolean; onSave: (v: number | null) => void }) {
  const [v, setV] = useState(value == null ? "" : String(value));
  useEffect(() => setV(value == null ? "" : String(value)), [value]);
  const n = v === "" ? null : Number(v);
  const dirty = n !== value;
  const ok = n == null || (Number.isInteger(n) && n >= 1 && n <= max);
  return (
    <form style={{ marginTop: 4 }} onSubmit={(e) => { e.preventDefault(); if (ok && dirty) onSave(n); }}>
      <input className="input mono" inputMode="numeric" aria-label={label} title={label} placeholder="∞" value={v} disabled={disabled}
        onChange={(e) => setV(e.target.value.replace(/\D/g, "").slice(0, 3))} onBlur={() => { if (ok && dirty) onSave(n); }}
        style={{ width: 60, minHeight: 26, padding: "2px 6px", fontSize: 12, textAlign: "center", ...(ok ? {} : { borderColor: "var(--bad)" }) }} />
      <div className="muted" style={{ fontSize: 10.5, lineHeight: 1.2, marginTop: 2 }}>{label}</div>
    </form>
  );
}

function Durations({ countryId, trialDays, graceDays, canEdit, onSaved }: { countryId: string; trialDays: number; graceDays: number; canEdit: boolean; onSaved: () => void }) {
  const { toast } = useStudio();
  const qc = useQueryClient();
  const [t, setT] = useState(String(trialDays));
  const [g, setG] = useState(String(graceDays));
  useEffect(() => { setT(String(trialDays)); setG(String(graceDays)); }, [trialDays, graceDays]);
  const save = useMutation({
    mutationFn: () => admin.updateCountry(countryId, { trialDays: Number(t), graceDays: Number(g) }),
    onSuccess: () => { toast("Durées enregistrées : appliquées tout de suite"); onSaved(); void qc.invalidateQueries({ queryKey: ["admin-countries"] }); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const valid = [t, g].every((x) => x !== "" && Number(x) >= 0 && Number(x) <= 60);
  const dirty = Number(t) !== trialDays || Number(g) !== graceDays;
  return (
    <form className="card row" style={{ gap: 16, alignItems: "flex-end", flexWrap: "wrap" }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <label className="stack" style={{ gap: 4 }}><span className="small"><b>Essai gratuit</b> (jours)</span>
        <input className="input mono" style={{ width: 90 }} inputMode="numeric" value={t} disabled={!canEdit} onChange={(e) => setT(e.target.value.replace(/\D/g, "").slice(0, 2))} /></label>
      <label className="stack" style={{ gap: 4 }}><span className="small"><b>Délai de grâce</b> (jours après la fin payée)</span>
        <input className="input mono" style={{ width: 90 }} inputMode="numeric" value={g} disabled={!canEdit} onChange={(e) => setG(e.target.value.replace(/\D/g, "").slice(0, 2))} /></label>
      <span className="muted small" style={{ flex: 1, minWidth: 220 }}>De 0 à 60 jours. Un changement vaut aussi pour les familles déjà inscrites.</span>
      {canEdit && <button className="btn" disabled={!valid || !dirty || save.isPending}>Enregistrer</button>}
    </form>
  );
}
